import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateIncreaseDepositLimitBlockMutation,
  PlayerUpdateIncreaseDepositLimitBlockMutationVariables,
} from './__generated__/PlayerUpdateIncreaseDepositLimitBlockForm';

const query = graphql`
  query SanityPlayerUpdateIncreaseDepositLimitBlockForm {
    sanityPlayerUpdateIncreaseDepositLimitBlockForm {
      title {
        ...SanityLocaleString
      }
      description {
        ...SanityLocaleString
      }
      isIncreaseDepositLimitBlocked {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const updateCustomerIncreaseDepositLimitBlockMutation = gql`
  mutation PlayerUpdateIncreaseDepositLimitBlock(
    $playerGlobalId: ID!
    $enable: Boolean!
  ) {
    updateCustomerIncreaseDepositLimitBlock(
      playerGlobalId: $playerGlobalId
      enable: $enable
    ) {
      ... on Customer {
        id
        isIncreaseDepositLimitBlocked
      }
    }
  }
`;

const PlayerUpdateIncreaseDepositLimitBlockForm: FC<{
  playerGlobalId: PlayerId;
  isBlocked: boolean;
}> = ({ playerGlobalId, isBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateIncreaseDepositLimitBlockFormQuery>(
      query,
    );

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateIncreaseDepositLimitBlockForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [
    updateIncreaseDepositLimitBlockState,
    updateIncreaseDepositLimitBlock,
  ] = useMutation<
    PlayerUpdateIncreaseDepositLimitBlockMutation,
    PlayerUpdateIncreaseDepositLimitBlockMutationVariables
  >(updateCustomerIncreaseDepositLimitBlockMutation);

  const defaultValues = {
    isIncreaseDepositLimitBlocked: isBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isIncreaseDepositLimitBlocked');
  const isUnchanged = nextValue === isBlocked;

  const onSubmit = ({
    isIncreaseDepositLimitBlocked,
  }: typeof defaultValues) => {
    updateIncreaseDepositLimitBlock({
      playerGlobalId,
      enable: isIncreaseDepositLimitBlocked,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <div className="space-y-1">
              <div className="block text-gray-600 text-sm dark:text-gray-200">
                {t(form.description)}
              </div>
            </div>
            <SwitchField
              title={t(form.isIncreaseDepositLimitBlocked)}
              name="isIncreaseDepositLimitBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value={t(form.submit)}
              disabled={
                updateIncreaseDepositLimitBlockState.fetching || isUnchanged
              }
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateIncreaseDepositLimitBlockForm;
